/**
 * Badge 색상 매핑.
 *
 * 색상은 반드시 디자인 시스템 토큰 (packages/runner-request/ui/color-tokens.css) 만 참조한다.
 * DaisyUI alpha 표기 (`bg-primary/20`) 나 하드코딩 hex 는 금지.
 *
 * - bg + fg 페어가 필요한 칩: chip.{name} 사용.
 * - 아이콘/텍스트 단독: var(--color-{name}) 을 arbitrary value 로 감싸 사용.
 */

const chip = {
  primary: 'bg-[var(--chip-primary-bg)] text-[var(--chip-primary-fg)]',
  secondary: 'bg-[var(--chip-secondary-bg)] text-[var(--chip-secondary-fg)]',
  success: 'bg-[var(--chip-success-bg)] text-[var(--chip-success-fg)]',
  warning: 'bg-[var(--chip-warning-bg)] text-[var(--chip-warning-fg)]',
  error: 'bg-[var(--chip-error-bg)] text-[var(--chip-error-fg)]',
  info: 'bg-[var(--chip-info-bg)] text-[var(--chip-info-fg)]',
  neutral: 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]',
} as const;

// 엔티티 배지 맵이 없는 범용 메타 배지(예: Markdown, 정적 라벨)용 tone. MetaBadge의 `tone`에 넘긴다.
export const badgeTone = chip;
export type BadgeTone = keyof typeof chip;

// 완료보고서의 코드리뷰 선언(reviewRecommendation) 배지. REQUIRED=경고색, NOT_NEEDED=중립.
export const reviewRecommendationBadge = {
  REQUIRED: chip.warning,
  NOT_NEEDED: chip.neutral,
} as const;

// 완료보고서의 진행형 리뷰 상태(reviewStatus) 배지. 연결된 코드리뷰 상태에서 파생된다.
// REQUIRED=리뷰 필요(경고) · IN_REVIEW=리뷰 중(정보) · COMPLETED=리뷰 완료(성공) · NOT_NEEDED=불필요(중립)
// · EXPIRED=리뷰 미처리 상태로 기간 경과(중립). 이미 큐에서 내려갔으므로 경고색을 쓰지 않는다.
export const reportReviewStatusBadge = {
  REQUIRED: chip.warning,
  IN_REVIEW: chip.info,
  COMPLETED: chip.success,
  NOT_NEEDED: chip.neutral,
  EXPIRED: chip.neutral,
} as const;

export type ReportReviewStatusBadge = (typeof reportReviewStatusBadge)[keyof typeof reportReviewStatusBadge];

export const badgeSizes = {
  sm: 'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
  xs: 'inline-flex items-center rounded-full px-1.5 py-px text-[10px] font-medium',
  xxs: 'inline-flex items-center rounded-full px-1 py-px text-[9px] font-medium',
} as const;

export const planStatusBadge = {
  BACKLOG: chip.neutral,
  TODO: chip.warning,
  IN_PROGRESS: chip.info,
  PARTIAL: chip.warning,
  ASSIGNED: chip.primary,
  DONE: chip.success,
  BLOCKED: chip.error,
  CANCELLED: chip.neutral,
} as const;

export type PlanStatusBadge = (typeof planStatusBadge)[keyof typeof planStatusBadge];

export const planStatusIconColor = {
  BACKLOG: 'text-[var(--color-fg-subtle)]',
  TODO: 'text-[var(--color-warning)]',
  ASSIGNED: 'text-[var(--color-primary)]',
  IN_PROGRESS: 'text-[var(--color-info)]',
  PARTIAL: 'text-[var(--color-warning)]',
  DONE: 'text-[var(--color-success)]',
  BLOCKED: 'text-[var(--color-error)]',
  CANCELLED: 'text-[var(--color-fg-subtle)]',
} as const;

// status → 진행 글리프 키 + 아이콘 컬러(text-*)의 SSOT. EntityStatusIcon/EntityStatusBadge/EntityStatusSelect가
// (entityType, status)로 글리프·컬러를 해석할 때 사용한다. 글리프 키는 EntityStatusIcon의 statusIconMap 키와 일치.
// PLAN은 planStatusIconColor에서 파생하고, 나머지는 홈 피드의 검증된 매핑을 정본화한 값이다. DOCUMENT/CONVENTION은 상태 개념이 없어 제외.
export type StatusIconVisual = { icon: string; color: string };

const planStatusIconVisual = Object.fromEntries(
  Object.entries(planStatusIconColor).map(([status, color]) => [status, { icon: status, color }]),
) as Record<string, StatusIconVisual>;
// PlanTask 전용 종료 상태. 플랜 상태엔 없지만 작업 상태 셀렉트/배지가 PLAN 레지스트리를 공유하므로 등록한다.
// (없으면 SKIPPED 작업의 글리프가 비어 트리거가 안 보이고 클릭·복구가 불가해진다.)
planStatusIconVisual.SKIPPED = { icon: 'SKIPPED', color: 'text-[var(--color-fg-subtle)]' };

export const STATUS_ICON_REGISTRY: Record<StatusBadgeEntityType, Record<string, StatusIconVisual>> = {
  PLAN: planStatusIconVisual,
  COMPLETION_REPORT: {
    COMPLETED: { icon: 'DONE', color: 'text-[var(--color-success)]' },
    PARTIAL: { icon: 'ASSIGNED', color: 'text-[var(--color-warning)]' },
    FAILED: { icon: 'BLOCKED', color: 'text-[var(--color-error)]' },
    CANCELLED: { icon: 'CANCELLED', color: 'text-[var(--color-fg-subtle)]' },
  },
  POST_MORTEM: {
    OPEN: { icon: 'TODO', color: 'text-[var(--color-warning)]' },
    IN_PROGRESS: { icon: 'IN_PROGRESS', color: 'text-[var(--color-info)]' },
    RESOLVED: { icon: 'DONE', color: 'text-[var(--color-success)]' },
  },
  CO_ACTION: {
    OPEN: { icon: 'TODO', color: 'text-[var(--color-info)]' },
    CLOSED: { icon: 'DONE', color: 'text-[var(--color-fg-subtle)]' },
  },
  CO_ACTION_SESSION: {
    RUNNING: { icon: 'IN_PROGRESS', color: 'text-[var(--color-info)]' },
    NEEDS_REVIEW: { icon: 'TODO', color: 'text-[var(--color-warning)]' },
    COMPLETED: { icon: 'DONE', color: 'text-[var(--color-success)]' },
    FAILED: { icon: 'BLOCKED', color: 'text-[var(--color-error)]' },
    CANCELLED: { icon: 'CANCELLED', color: 'text-[var(--color-fg-subtle)]' },
    UNKNOWN: { icon: 'BACKLOG', color: 'text-[var(--color-fg-subtle)]' },
  },
  CODE_REVIEW: {
    PENDING: { icon: 'BACKLOG', color: 'text-[var(--color-warning)]' },
    OPEN: { icon: 'IN_PROGRESS', color: 'text-[var(--color-info)]' },
    PARTIAL: { icon: 'ASSIGNED', color: 'text-[var(--color-secondary)]' },
    COMPLETED: { icon: 'DONE', color: 'text-[var(--color-success)]' },
    CANCELLED: { icon: 'CANCELLED', color: 'text-[var(--color-fg-subtle)]' },
    FAILED: { icon: 'BLOCKED', color: 'text-[var(--color-error)]' },
  },
};

// entityType이 상태 미보유 타입(DOCUMENT/CONVENTION 등)이거나 미지 status면 null을 반환한다.
export const getStatusIconVisual = (entityType: string, status: string): StatusIconVisual | null =>
  (STATUS_ICON_REGISTRY as Record<string, Record<string, StatusIconVisual>>)[entityType]?.[status] ?? null;

export const planStatusLabel: Record<keyof typeof planStatusBadge, string> = {
  BACKLOG: 'Backlog',
  TODO: 'Todo',
  IN_PROGRESS: 'In Progress',
  PARTIAL: 'Partial',
  ASSIGNED: 'Assigned',
  DONE: 'Done',
  BLOCKED: 'Blocked',
  CANCELLED: 'Cancelled',
} as const;

export const planPriorityBadge = {
  HIGH: chip.error,
  MEDIUM: chip.warning,
  LOW: chip.neutral,
} as const;

export type PlanPriorityBadge = (typeof planPriorityBadge)[keyof typeof planPriorityBadge];

export const planTypeBadge = {
  FEATURE: chip.primary,
  BUG_FIX: chip.error,
  ISSUE: chip.warning,
  REFACTOR: chip.info,
  CHORE: chip.neutral,
} as const;

export type PlanTypeBadge = (typeof planTypeBadge)[keyof typeof planTypeBadge];

export const planComplexityBadge = {
  MINIMAL: chip.neutral,
  STANDARD: chip.info,
  FULL: chip.secondary,
} as const;

export type PlanComplexityBadge = (typeof planComplexityBadge)[keyof typeof planComplexityBadge];

export const planComplexityLabel = {
  MINIMAL: 'Minimal',
  STANDARD: 'Standard',
  FULL: 'Full',
} as const;

export const commentTypeBadge = {
  RISK: chip.error,
  MODIFICATION: chip.warning,
  GENERAL: chip.neutral,
} as const;

export type CommentTypeBadge = (typeof commentTypeBadge)[keyof typeof commentTypeBadge];

export const conventionCategoryBadge = {
  rules: chip.info,
  skills: chip.success,
  guides: chip.warning,
  references: chip.neutral,
} as const;

export type ConventionCategoryBadge = (typeof conventionCategoryBadge)[keyof typeof conventionCategoryBadge];

export const conventionScopeBadge = {
  PERSONAL: chip.secondary,
  PROJECT: chip.primary,
} as const;

export type ConventionScopeBadge = (typeof conventionScopeBadge)[keyof typeof conventionScopeBadge];

export const conventionTriggerBadge = {
  always_on: chip.info,
  model_decision: chip.warning,
} as const;

export type ConventionTriggerBadge = (typeof conventionTriggerBadge)[keyof typeof conventionTriggerBadge];

export const postMortemStatusBadge = {
  OPEN: chip.warning,
  IN_PROGRESS: chip.info,
  RESOLVED: chip.success,
} as const;

export type PostMortemStatusBadge = (typeof postMortemStatusBadge)[keyof typeof postMortemStatusBadge];

export const postMortemStatusLabel: Record<keyof typeof postMortemStatusBadge, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  RESOLVED: 'Resolved',
} as const;

export const coActionStatusBadge = {
  OPEN: chip.info,
  CLOSED: chip.neutral,
} as const;

export type CoActionStatusBadge = (typeof coActionStatusBadge)[keyof typeof coActionStatusBadge];

export const coActionStatusLabel: Record<keyof typeof coActionStatusBadge, string> = {
  OPEN: 'Open',
  CLOSED: 'Closed',
} as const;

export const coActionSessionStatusBadge = {
  RUNNING: chip.info,
  NEEDS_REVIEW: chip.warning,
  COMPLETED: chip.success,
  FAILED: chip.error,
  CANCELLED: chip.neutral,
  UNKNOWN: chip.neutral,
} as const;

export const coActionSessionStatusLabel: Record<keyof typeof coActionSessionStatusBadge, string> = {
  RUNNING: 'Running',
  NEEDS_REVIEW: 'Needs review',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
  UNKNOWN: 'Unknown',
} as const;

export const coActionVisibilityBadge = {
  PRIVATE: chip.neutral,
  PROJECT: chip.primary,
} as const;

export const documentVisibilityBadge = {
  PRIVATE: chip.neutral,
  PROJECT: chip.primary,
} as const;

export const planVisibilityBadge = {
  PRIVATE: chip.neutral,
  PROJECT: chip.primary,
} as const;

export const documentArchivedBadge = chip.warning;

export type CoActionVisibilityBadge = (typeof coActionVisibilityBadge)[keyof typeof coActionVisibilityBadge];

export const coActionSourceBadge = {
  MANUAL: chip.success,
  AUTO_SESSION: chip.warning,
} as const;

export type CoActionSourceBadge = (typeof coActionSourceBadge)[keyof typeof coActionSourceBadge];

export const completionReportStatusBadge = {
  COMPLETED: chip.success,
  FAILED: chip.error,
  PARTIAL: chip.warning,
  CANCELLED: chip.neutral,
} as const;

export type CompletionReportStatusBadge =
  (typeof completionReportStatusBadge)[keyof typeof completionReportStatusBadge];

export const completionReportStatusLabel: Record<keyof typeof completionReportStatusBadge, string> = {
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  PARTIAL: 'Partial',
  CANCELLED: 'Cancelled',
} as const;

export const codeReviewStatusBadge = {
  PENDING: chip.warning,
  OPEN: chip.info,
  PARTIAL: chip.secondary,
  COMPLETED: chip.success,
  CANCELLED: chip.neutral,
  FAILED: chip.error,
} as const;

export type CodeReviewStatusBadge = (typeof codeReviewStatusBadge)[keyof typeof codeReviewStatusBadge];

// 형제 엔티티와 동일하게 목록/상세/홈 활동 필터/검색 모달 모두 정적 라벨 맵을 사용한다.
export const codeReviewStatusLabel: Record<keyof typeof codeReviewStatusBadge, string> = {
  PENDING: 'Pending',
  OPEN: 'Open',
  PARTIAL: 'Partial',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  FAILED: 'Failed',
} as const;

// 상태 보유 엔티티의 status→{톤, 라벨} 레지스트리. EntityStatusBadge가 (entityType, status)로
// 톤·라벨을 자동 해석할 때 사용한다. 값은 위에 정의된 기존 맵을 그대로 묶기만 한다(재정의 금지).
// DOCUMENT/CONVENTION은 상태 개념이 없어 제외한다.
export const STATUS_BADGE_REGISTRY = {
  PLAN: { badge: planStatusBadge, label: planStatusLabel },
  COMPLETION_REPORT: { badge: completionReportStatusBadge, label: completionReportStatusLabel },
  POST_MORTEM: { badge: postMortemStatusBadge, label: postMortemStatusLabel },
  CO_ACTION: { badge: coActionStatusBadge, label: coActionStatusLabel },
  CO_ACTION_SESSION: { badge: coActionSessionStatusBadge, label: coActionSessionStatusLabel },
  CODE_REVIEW: { badge: codeReviewStatusBadge, label: codeReviewStatusLabel },
} as const;

export type StatusBadgeEntityType = keyof typeof STATUS_BADGE_REGISTRY;

export const codeReviewSeverityBadge = {
  P0: chip.error,
  P1: chip.warning,
  P2: chip.info,
  P3: chip.neutral,
} as const;

export type CodeReviewSeverityBadge = (typeof codeReviewSeverityBadge)[keyof typeof codeReviewSeverityBadge];

// finding 카드 좌측 액센트 보더. 심각도를 한눈에 스캔할 수 있도록 chip 과 동일한 색 계열을 사용한다.
export const codeReviewSeverityBorder: Record<keyof typeof codeReviewSeverityBadge, string> = {
  P0: 'border-l-[var(--color-error)]',
  P1: 'border-l-[var(--color-warning)]',
  P2: 'border-l-[var(--color-info)]',
  P3: 'border-l-[var(--color-fg-subtle)]',
} as const;

export const codeReviewFindingStatusBadge = {
  OPEN: chip.info,
  PLANNED: chip.success,
  DISMISSED: chip.neutral,
  RESOLVED: chip.success,
} as const;

export type CodeReviewFindingStatusBadge =
  (typeof codeReviewFindingStatusBadge)[keyof typeof codeReviewFindingStatusBadge];

export const codeReviewFindingStatusLabel: Record<keyof typeof codeReviewFindingStatusBadge, string> = {
  OPEN: 'Open',
  PLANNED: 'Planned',
  DISMISSED: 'Dismissed',
  RESOLVED: 'Resolved',
} as const;

export const daemonTriggerStatusBadge = {
  PENDING: chip.warning,
  IN_PROGRESS: chip.info,
  CANCEL_REQUESTED: chip.warning,
  DONE: chip.success,
  CANCELLED: chip.neutral,
  FAILED: chip.error,
  REJECTED: chip.neutral,
  NEEDS_REVIEW: chip.secondary,
} as const;

export type DaemonTriggerStatusBadge = (typeof daemonTriggerStatusBadge)[keyof typeof daemonTriggerStatusBadge];

export const daemonTriggerStatusLabel: Record<keyof typeof daemonTriggerStatusBadge, string> = {
  PENDING: 'Pending',
  IN_PROGRESS: 'Running',
  CANCEL_REQUESTED: 'Cancelling',
  DONE: 'Done',
  CANCELLED: 'Cancelled',
  FAILED: 'Failed',
  REJECTED: 'Rejected',
  NEEDS_REVIEW: 'Needs Review',
} as const;

export const worktreeStatusBadge = {
  CREATING: chip.warning,
  ACTIVE: chip.info,
  REMOVE_REQUESTED: chip.warning,
  REMOVING: chip.warning,
  REMOVED: chip.neutral,
  FAILED: chip.error,
} as const;

export type WorktreeStatusBadge = (typeof worktreeStatusBadge)[keyof typeof worktreeStatusBadge];

export const daemonTriggerLogLevelBadge = {
  INFO: 'text-[var(--color-info)]',
  WARN: 'text-[var(--color-warning)]',
  ERROR: 'text-[var(--color-error)]',
} as const;

export type DaemonTriggerLogLevelBadge = (typeof daemonTriggerLogLevelBadge)[keyof typeof daemonTriggerLogLevelBadge];

export function qualityScoreBadge(score: number): string {
  if (score >= 90) return 'text-[var(--color-success)]';
  if (score >= 70) return 'text-[var(--color-warning)]';
  return 'text-[var(--color-error)]';
}

export const memberTierBadge = {
  PRO: chip.success,
} as const;

export const memberTierDefaultBadge = chip.neutral;

// 피드백 상태 칩 톤. 카드 편집 칩과 상세 패널/모달이 공유하는 단일 소스.
export const feedbackStatusBadge = {
  OPEN: chip.warning,
  IN_PROGRESS: chip.info,
  RESOLVED: chip.success,
  DISMISSED: chip.neutral,
} as const;

export const getFeedbackStatusTone = (status: string): string =>
  feedbackStatusBadge[status as keyof typeof feedbackStatusBadge] ?? chip.neutral;

export interface QualityScoreGrade {
  grade: 'PERFECT' | 'EXCELLENT' | 'GOOD' | 'NEEDS_WORK' | 'REWORK' | 'NONE';
  label: string;
  badgeClass: string;
  iconName: string;
  iconColorClass: string;
}

export function getQualityScoreGrade(score: number | null | undefined): QualityScoreGrade {
  if (score === null || score === undefined) {
    return {
      grade: 'NONE',
      label: '-',
      badgeClass: chip.neutral,
      iconName: 'Minus',
      iconColorClass: 'text-[var(--color-fg-subtle)]',
    };
  }

  if (score >= 100) {
    return {
      grade: 'PERFECT',
      label: '최상',
      badgeClass: chip.success,
      iconName: 'Award',
      iconColorClass: 'text-[var(--color-success)]',
    };
  }
  if (score >= 95) {
    return {
      grade: 'EXCELLENT',
      label: '우수',
      badgeClass: chip.success,
      iconName: 'CheckCircle2',
      iconColorClass: 'text-[var(--color-success)]',
    };
  }
  if (score >= 90) {
    return {
      grade: 'GOOD',
      label: '양호',
      badgeClass: chip.info,
      iconName: 'Check',
      iconColorClass: 'text-[var(--color-info)]',
    };
  }
  if (score >= 80) {
    // 80–89는 가이드상 "사소한 갭" 구간 — 경고가 아니라 중립으로 표기한다.
    return {
      grade: 'NEEDS_WORK',
      label: '보통',
      badgeClass: chip.neutral,
      iconName: 'Minus',
      iconColorClass: 'text-[var(--color-fg-subtle)]',
    };
  }
  // 80 미만에서만 주의 신호(경고)를 사용한다.
  return {
    grade: 'REWORK',
    label: '보완 필요',
    badgeClass: chip.warning,
    iconName: 'AlertTriangle',
    iconColorClass: 'text-[var(--color-warning)]',
  };
}
